/**
 * 案件の差分（欄ごとの変更前・変更後）を組む純関数。
 *
 * **画面と MCP が同じ 1 関数を通す。** コンソールの `getPjDiff` と MCP の
 * `get_spec_changes(pj_number, fields=true)` は、どちらも案件の変更の行をここへ渡して欄の
 * 並びと「変わったか」を受け取る。入口ごとに比べ方を書くと、同じ案件で画面と MCP の答えが
 * ずれる（`CLAUDE.md`「判定は 1 本の API に集約」）。
 *
 * **変更前は行の `before` である。** 未確定のあいだ master とずれていれば、それは土台ずれとして
 * 確定で止まる（変更履歴 §3.4）ので、差分は行の前後だけで組む。
 *
 * **業務ルールの中身は、行ごとに欄を分ける。** 中身を 1 欄のまま比べると、1 行足しただけで
 * 中身全体が「変わった」になり、どの行が変わったかが読めない。判定表は行（`body.rows.<行 id>`）、
 * 式は段（`body.steps.<段 id>`）、並べ替えはキーの行（`並べ替え: <属性>`）とキーの決め方
 * （`body.derive.<キー>.…`）へ展開し、残りの欄は `body.<欄>` にする。前後で同じ鍵の欄どうしを揃える。
 * 欄の名前の読み方は `parsePjDiffField` 1 つに置く（画面はこれで欄を区画に分ける）。
 *
 * **欄の並びは変更後を基準にし、変更前にしか無い欄（消したキーの行）は変更前で並んでいた位置へ
 * 差し込む**（git の差分と同じ見え方）。後ろへ回すと、左右の同じ位置に別のキーが並ぶ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行の前後を欄ごとに突き合わせ、ノードごとの差分（変更の種類・名前・欄の前後と変わったか）を組む
 * @graph-connects none
 */
/**
 * 差分の 1 ノードが表す変更の種類 3 値。行の前後の null だけから決まる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の差分の変更の種類（追加・更新・削除）の閉集合
 * @graph-connects none
 */
export const PJ_DIFF_CHANGE_KINDS = ['add', 'update', 'delete'];
/**
 * 並べ替えの行を展開した欄の名前の前置き。後ろに行の属性（プロパティの用語の id）が付く。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの形の業務ルールの行を欄へ展開するときの欄名の前置き
 * @graph-connects none
 */
export const PJ_DIFF_SORT_FIELD_PREFIX = '並べ替え: ';
/** 画面に出さない欄の名前。 @graph-connects none */
const PJ_DIFF_HIDDEN_FIELDS = new Set(['id', 'sourceRules']);
/**
 * 差分の欄のうち画面に出さないものか。**画面の表（出さない）と読取の名前の表（番号を数えない）が
 * 同じこの 1 つを通す**（片方だけ足すと、出さない欄の番号を引きに master を読むか、出す欄の番号が
 * 名前にならないかのどちらかが黙って起きる）。
 *
 * - `id` ノード自身の番号。変わらない値で、見出しの行と「詳細で見る」が既に指している
 * - `sourceRules` 元の規則。規則の番号は集約の中だけで通じる局所の番号で、要素の番号ではなく、
 *   画面にも出さない（設計書 api §3.3。`element-read.ts` の参照の閉包も辿らない）
 *
 * @param field 差分の欄の名前
 * @returns 画面に出さない欄なら true
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の差分の欄のうち、画面に出さず名前を引く番号にも数えない欄（要素の番号・元の規則）かを判定する
 * @graph-connects none
 */
export function isPjDiffHiddenField(field) {
    return PJ_DIFF_HIDDEN_FIELDS.has(field);
}
/** 欄の名前の読み方（上から順に当てる）。 @graph-connects none */
const PJ_DIFF_FIELD_PATTERNS = [
    [/^並べ替え: (\d+)$/u, (match) => ({ part: 'sortKey', attribute: Number(match[1]) })],
    [
        /^body\.derive\.(\d+)\.rows\.(\d+)$/u,
        (match) => ({ part: 'deriveRow', key: Number(match[1]), rowId: Number(match[2]) }),
    ],
    [
        /^body\.derive\.(\d+)\.steps\.(\d+)$/u,
        (match) => ({ part: 'deriveStep', key: Number(match[1]), stepId: Number(match[2]) }),
    ],
    [
        /^body\.derive\.(\d+)\.(\w+)$/u,
        (match) => ({ part: 'derive', key: Number(match[1]), name: match[2] ?? '' }),
    ],
    [/^body\.rows\.(\d+)$/u, (match) => ({ part: 'row', rowId: Number(match[1]) })],
    [/^body\.steps\.(\d+)$/u, (match) => ({ part: 'step', stepId: Number(match[1]) })],
    [/^body\.(\w+)$/u, (match) => ({ part: 'body', name: match[1] ?? '' })],
];
/**
 * 差分の欄の名前から、それが指す場所を読む。どの形にも当たらなければノードの欄とみなす。
 *
 * @param field 差分の欄の名前（`PjDiffField.field`）
 * @returns 欄が指す場所
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の差分の欄の名前を、ノードの欄・中身の欄・判定表の行・式の段・並べ替えのキー・キーの決め方の場所へ読み解く
 * @graph-connects none
 */
export function parsePjDiffField(field) {
    for (const [pattern, toPath] of PJ_DIFF_FIELD_PATTERNS) {
        const match = pattern.exec(field);
        if (match !== null)
            return toPath(match);
    }
    return { part: 'property', name: field };
}
/** 配列でないオブジェクトか。 @graph-connects none */
function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
/**
 * 行の値を読み解く。**読めない値は握りつぶさず投げる。**
 *
 * この値を書いたのはこのシステム自身なので、読めないのはデータの不整合である。空で代替すると
 * 「欄が無かった」と「読めなかった」が差分から区別できなくなる。
 * @graph-connects none
 */
function decodeValue(subject, raw) {
    if (raw === null)
        return null;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!isRecord(parsed) || typeof parsed['nodeType'] !== 'string') {
        throw new Error(`spec-graph: ${subject} の値に型（nodeType）が無い`);
    }
    const properties = parsed['properties'];
    if (!isRecord(properties)) {
        throw new Error(`spec-graph: ${subject} の値に欄（properties）が無い`);
    }
    return { nodeType: parsed['nodeType'], properties };
}
/** 鍵の欄を除いたオブジェクト。 @graph-connects none */
function without(record, name) {
    return Object.fromEntries(Object.entries(record).filter(([key]) => key !== name));
}
/**
 * 並びを、各要素の鍵の欄（行 id・段 id・属性・キー）で引ける対の並びにする。欄が無ければ空の並び。
 *
 * **鍵が 1 つでも欠けるか 2 つ以上重なれば展開しない（null）。** 欄名は鍵で決まるので、重なれば
 * 後の要素が前の要素を上書きし、1 行が差分から黙って消える。書込のスキーマは属性の重複を
 * 拒まないので例外にもしない — 1 欄のまま比べれば、どの要素も差分に残る。
 * @graph-connects isRecord [calls] 要素がオブジェクトか
 * @graph-connects without [calls] 鍵の欄を除く
 */
function keyedRecords(raw, keyName) {
    if (raw === undefined)
        return [];
    if (!Array.isArray(raw))
        return null;
    const entries = raw.map((entry) => {
        const key = isRecord(entry) ? entry[keyName] : undefined;
        return typeof key === 'number' && Number.isInteger(key) && isRecord(entry)
            ? [key, without(entry, keyName)]
            : null;
    });
    if (entries.some((entry) => entry === null))
        return null;
    const keyed = entries.filter((entry) => entry !== null);
    return new Set(keyed.map(([key]) => key)).size === keyed.length ? keyed : null;
}
/**
 * 中身の欄を、行・段を割った欄の対へ写す。`rows` ／ `steps` は鍵で割り、ほかは `<前置き><欄>` にする。
 * 割れない（鍵が欠ける・重なる）行・段は 1 欄のまま残す。
 * @graph-connects keyedRecords [calls] 行・段を鍵で引く
 */
function splitRowsAndSteps(body, prefix) {
    return Object.entries(body).flatMap(([name, value]) => {
        const lines = name === 'rows' || name === 'steps' ? keyedRecords(value, 'id') : null;
        if (lines === null)
            return [[`${prefix}${name}`, value]];
        return lines.map(([id, line]) => [`${prefix}${name}.${String(id)}`, line]);
    });
}
/**
 * キーの決め方 1 つを欄の対へ写す。形は `body.derive.<キー>.kind`、中身は行・段を割って
 * `body.derive.<キー>.…` にする。
 * @graph-connects isRecord [calls] 中身がオブジェクトか
 * @graph-connects splitRowsAndSteps [calls] 中身の行・段を割る
 */
function deriveFields(key, derive) {
    const prefix = `body.derive.${String(key)}.`;
    const body = derive['body'];
    return [
        [`${prefix}kind`, derive['kind']],
        ...(isRecord(body) ? splitRowsAndSteps(body, prefix) : [[`${prefix}body`, body]]),
    ];
}
/**
 * 業務ルールの中身を欄の対へ写す。判定表の行・式の段は鍵で、並べ替えのキーの行は属性で、キーの
 * 決め方はキーで割る。割れない並びは 1 欄のまま残す。
 * @graph-connects isRecord [calls] 中身がオブジェクトか
 * @graph-connects keyedRecords [calls] 並べ替えのキーの行とキーの決め方を鍵で引く
 * @graph-connects splitRowsAndSteps [calls] 判定表の行・式の段を割る
 * @graph-connects deriveFields [calls] キーの決め方を割る
 */
function ruleBodyFields(body) {
    if (!isRecord(body))
        return [['body', body]];
    const keys = keyedRecords(body['keys'], 'attribute');
    const derives = keyedRecords(body['derive'], 'key');
    const rest = Object.fromEntries(Object.entries(body).filter(([name]) => !(name === 'keys' && keys !== null) && !(name === 'derive' && derives !== null)));
    return [
        ...splitRowsAndSteps(rest, 'body.'),
        ...(keys ?? []).map(([attribute, line]) => [`${PJ_DIFF_SORT_FIELD_PREFIX}${String(attribute)}`, line]),
        ...(derives ?? []).flatMap(([key, derive]) => deriveFields(key, derive)),
    ];
}
/**
 * 値を欄の並びにする。業務ルールの中身は行・段・キーの行・キーの決め方へ展開する。
 * @graph-connects ruleBodyFields [calls] 業務ルールの中身を割る
 */
function fieldsOf(value) {
    const fields = new Map();
    if (value === null)
        return fields;
    for (const [key, field] of Object.entries(value.properties)) {
        if (key === 'body' && value.nodeType === 'Rule') {
            for (const [name, part] of ruleBodyFields(field))
                fields.set(name, part);
            continue;
        }
        fields.set(key, field);
    }
    return fields;
}
/**
 * 変更前と変更後の欄の名前を 1 本の並びにする。**変更後の並びを基準に、変更前にしか無い欄は、
 * 変更前でその直前にあった欄の後ろへ差し込む**（直前が無ければ先頭）。
 *
 * 欄の名前は各側で一意（行・段・キーは鍵で割ってある）なので、同じ名前どうしを揃えるだけで左右が
 * 並ぶ。消したキーの行を後ろへ回すと、左右の同じ位置に別のキーが並んで見える。
 * @graph-connects none
 */
function mergeFieldNames(before, after) {
    const merged = [...after];
    let anchor = null;
    for (const name of before) {
        if (!merged.includes(name)) {
            merged.splice(anchor === null ? 0 : merged.indexOf(anchor) + 1, 0, name);
        }
        anchor = name;
    }
    return merged;
}
/** 鍵の順を揃えた値。配列の並びは意味を持つので保つ。 @graph-connects none */
function canonical(value) {
    if (Array.isArray(value))
        return value.map(canonical);
    if (!isRecord(value))
        return value;
    return Object.fromEntries(Object.keys(value)
        .sort()
        .map((key) => [key, canonical(value[key])]));
}
/** 比べるための文字列。値が無い欄は null と同じに扱う。 @graph-connects none */
function comparable(value) {
    return JSON.stringify(canonical(value ?? null));
}
/**
 * 値の `label` を表示言語へ畳む。3 言語の組ならその言語、文字列ならそのまま、それ以外は空文字。
 * @graph-connects none
 */
function labelOf(value, locale) {
    const label = value?.properties['label'];
    if (typeof label === 'string')
        return label;
    if (!isRecord(label))
        return '';
    const text = label[locale];
    return typeof text === 'string' ? text : '';
}
/** 行の前後の null から変更の種類を決める。 @graph-connects none */
function changeKindOf(before, after) {
    if (before === null)
        return 'add';
    return after === null ? 'delete' : 'update';
}
/**
 * 案件の変更の行から、ノードごとの欄の差分を組む。
 *
 * 欄は変更前と変更後の欄名の和集合で、変更後の順に並べ、変更前にしか無い欄は変更前で直前に
 * あった欄の後ろへ差し込む。その側に欄が無ければ値は null になる。前後の比較は JSON の鍵の順を
 * 揃えた文字列で行う — 鍵の順だけが違う値を「変わった」と出さないためである。
 *
 * 名前は値の `label` から取る（変更後を優先）。`label` を持たない型は参照先の語から名前を組む
 * ので、ここでは空文字になる。ノードは id の順に並ぶ。
 *
 * @param rows 案件の変更の行（`listPjChanges` の行、または API が読み解いた行）
 * @param options 名前を畳む表示言語
 * @returns ノードごとの差分（id の順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行の前後を欄ごとに突き合わせ、変わった欄が分かるノードごとの差分を組む
 * @graph-connects decodeValue [calls] 行の値を読み解く
 * @graph-connects fieldsOf [calls] 値を欄の並びにする（業務ルールの中身は行・段・キーへ展開）
 * @graph-connects mergeFieldNames [calls] 前後の欄の並びを変更後基準で揃える
 * @graph-connects comparable [calls] 前後の値を比べる文字列
 * @graph-connects labelOf [calls] 名前を表示言語へ畳む
 * @graph-connects changeKindOf [calls] 変更の種類
 */
export function buildPjDiff(rows, options = {}) {
    const locale = options.locale ?? 'ja';
    return [...rows]
        .sort((left, right) => left.nodeId - right.nodeId)
        .map((row) => {
        const subject = `ノード ${String(row.nodeId)}`;
        const before = decodeValue(`${subject} の変更前`, row.before);
        const after = decodeValue(`${subject} の変更後`, row.after);
        const beforeFields = fieldsOf(before);
        const afterFields = fieldsOf(after);
        const names = mergeFieldNames([...beforeFields.keys()], [...afterFields.keys()]);
        return {
            nodeId: row.nodeId,
            nodeType: (after ?? before)?.nodeType ?? '',
            label: labelOf(after, locale) || labelOf(before, locale),
            changeKind: changeKindOf(before, after),
            status: row.status,
            fields: names.map((field) => {
                const beforeValue = beforeFields.get(field) ?? null;
                const afterValue = afterFields.get(field) ?? null;
                return {
                    field,
                    before: beforeValue,
                    after: afterValue,
                    changed: comparable(beforeValue) !== comparable(afterValue),
                };
            }),
        };
    });
}
//# sourceMappingURL=pj-diff.js.map